import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react'
import {
  publicGetIcalFeedsApi,
  publicSynchronizeIcalFeedsApi,
} from 'src/services/api/icalFeed'
import {publicGetBookingPeriodsApi} from 'src/services/api/property'
import {
  CalendarValues,
  IcalFeedType,
  BookingPeriodValues,
  RentalParametersFormValues,
  IcalFeedsData,
  DatesStatusMap,
} from 'src/types/form'
import {publicGetCalendarsApi} from 'src/services/api/calendar'
import {PropertyType} from 'src/types/property'
import {extractIcalFeedsData} from 'src/utils/ical'
import {SelectDateType} from 'src/types/utils'
import {getDatesStatusMap} from 'src/components/elements/dateRangeCalendar/data'
import {useVisibleMonths} from 'src/utils/calendar'
import {dayjs} from 'src/utils/date'
import {usePreviousBookingInformationContext} from 'src/context/PreviousBookingInformationProvider'
import {parallel} from 'src/utils/other'

interface CalendarContextProps {
  calendars: CalendarValues[]
  blockPeriodCalendars: CalendarValues[]
  notBlockPeriodCalendars: CalendarValues[]
  icalFeeds: IcalFeedType[]
  visibleMonths: string[]
  setVisibleMonths: (months: string[]) => void
  bookingPeriods: BookingPeriodValues[]
  rentalParameter?: RentalParametersFormValues
  icalFeedsData: IcalFeedsData
  datesStatusMap?: DatesStatusMap
  selectedDates: SelectDateType
  loading: boolean
  property: PropertyType
  setSelectedDates: (dates: SelectDateType) => void
  previousMonth: () => void
  nextMonth: () => void
}

type CalendarDataType = {
  loading: boolean
  calendars: CalendarValues[]
  blockPeriodCalendars: CalendarValues[]
  notBlockPeriodCalendars: CalendarValues[]
  icalFeeds: IcalFeedType[]
  bookingPeriods: BookingPeriodValues[]
}

export const CalendarContext = createContext<CalendarContextProps | undefined>(
  undefined,
)

CalendarContext.displayName = 'CalendarContext'

export const useCalendarContext = () => {
  const context = useContext(CalendarContext)
  if (context === undefined)
    throw new Error(
      `${CalendarContext.displayName} must be used within provider`,
    )

  return context
}

export default function CalendarProvider(props: {
  property: PropertyType
  children: React.ReactChild
}) {
  const {property} = props
  const propertyId = property.id

  const [data, setData] = useState<CalendarDataType>({
    loading: true, //init to true because we are loading
    calendars: [],
    blockPeriodCalendars: [],
    notBlockPeriodCalendars: [],
    icalFeeds: [],
    bookingPeriods: [],
  })

  //todo use previous dates ??
  const {setPreviousDates} = usePreviousBookingInformationContext()

  const [selectedDates, setSelectedDates] = useState<SelectDateType>([
    null,
    null,
  ])

  const {visibleMonths, setVisibleMonths, nextMonth, previousMonth} =
    useVisibleMonths()

  const handleSelectNewDates = useCallback(
    (dates: SelectDateType) => {
      setSelectedDates(dates)
      setPreviousDates(dates)
    },
    [setPreviousDates],
  )

  // const datesStatusMapBaseRef = useRef<DatesStatusMap>({})

  const fetchPropertyData = useCallback(async () => {
    if (!propertyId) {
      return
    }
    setData({
      loading: true,
      calendars: [],
      blockPeriodCalendars: [],
      notBlockPeriodCalendars: [],
      icalFeeds: [],
      bookingPeriods: [],
    })
    // datesStatusMapBaseRef.current = {} //reset since we change property

    const [bookingPeriods, icalFeeds, calendars] = await parallel(
      publicGetBookingPeriodsApi(propertyId),
      publicGetIcalFeedsApi(propertyId),
      publicGetCalendarsApi(propertyId),
    )

    let blockPeriodCalendarsLocal: CalendarValues[] = []
    let notBlockPeriodCalendarsLocal: CalendarValues[] = []
    calendars.forEach((calendar: CalendarValues) => {
      calendar.start = dayjs(calendar.start)
      calendar.end = dayjs(calendar.end)
      if (calendar.is_block_period) {
        blockPeriodCalendarsLocal.push(calendar)
      } else {
        notBlockPeriodCalendarsLocal.push(calendar)
      }
    })

    setData({
      loading: false,
      calendars,
      blockPeriodCalendars: blockPeriodCalendarsLocal,
      notBlockPeriodCalendars: notBlockPeriodCalendarsLocal,
      icalFeeds,
      bookingPeriods,
    })

    publicSynchronizeIcalFeedsApi(propertyId)
  }, [propertyId])

  useEffect(() => {
    fetchPropertyData()
  }, [fetchPropertyData])

  // parse ical feeds
  const icalFeedsData = useMemo<IcalFeedsData>(() => {
    if (data.loading) {
      return {
        loaded: false,
        icals: [],
        icalsMap: {},
      }
    }
    const icalFeedsData = extractIcalFeedsData(data.icalFeeds)
    return {
      loaded: true,
      icals: icalFeedsData.icals,
      icalsMap: icalFeedsData.icalsMap,
    }
  }, [data])

  const datesStatusMap: DatesStatusMap = useMemo(() => {
    if (data.loading) {
      //make sure to dont return a ref, but a value
      return {
        // ...datesStatusMapBaseRef.current,
      }
    }

    //always calculate the next/previous one, so when we click nextMonth, we already calculated it
    //let monthsToCalculate = [
    //dayjs(visibleMonths[0], 'YYYY-MM').subtract(1, 'month').format('YYYY-MM'),
    // ...visibleMonths,
    //dayjs(visibleMonths[1], 'YYYY-MM').add(1, 'month').format('YYYY-MM'),
    //]

    let map = getDatesStatusMap(
      visibleMonths,
      data.blockPeriodCalendars,
      data.notBlockPeriodCalendars,
      icalFeedsData.icals,
      icalFeedsData.icalsMap,
      data.bookingPeriods,
      property,
    )

    //datesStatusMapBaseRef.current = map

    return map
  }, [data, icalFeedsData, visibleMonths, property])

  return (
    <CalendarContext.Provider
      value={{
        property,
        visibleMonths,
        calendars: data.calendars,
        blockPeriodCalendars: data.blockPeriodCalendars,
        notBlockPeriodCalendars: data.notBlockPeriodCalendars,
        icalFeeds: data.icalFeeds,
        bookingPeriods: data.bookingPeriods,
        loading: data.loading,
        datesStatusMap,
        icalFeedsData,
        selectedDates,
        nextMonth,
        previousMonth,
        setSelectedDates: handleSelectNewDates,
        rentalParameter: property.rental_parameter,
        setVisibleMonths,
      }}
    >
      {props.children}
    </CalendarContext.Provider>
  )
}
